import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { t } from '../../../../i18n';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 任务卡展示文案的本地拼装。
 *
 * 上游 swiftx-basic 不下发成品文案（只给 code + 数值），三语文案由前端渲染，
 * 因此原先直接取用的 `volumeText` / `windowText` / `progressText` / `deadlineLabel`
 * 后端恒为 null，一律改由本文件按结构化字段拼出。
 *
 * todo hetianyang 当前文案是**临时占位**，与设计稿有差距，等产品给定稿后替换：
 *   · 设计稿 volumeText 为「142 件 · 8 条路线」，上游只有总件数，**没有路线数**，故只能出「142 件」
 *   · 设计稿 progressText 为「8 条路线已核 4 条 · 已扫 62 件」，同样缺路线维度，只能出「62 / 142 件」
 *   · 设计稿 deadlineLabel 为「装车 10:30 截止」，「装车」是任务类型专属前缀，
 *     若要还原需按 taskCode 逐个配文案，待产品确认各 taskCode 的前缀用词
 */

/**
 * 任务标题。人工指派任务的标题是派单人现填的自由文本，只能用上游 `title`；
 * 模板任务的 `title` 是 basic 侧硬编码中文（后端已置空），按 `taskCode` 出三语。
 *
 * 人工指派的 `taskCode`（= 上游 subType）恒为 `GENERAL`，不是文案主键，
 * 按它取 i18n 会把裸 code 显示给仓管，故 `title` 优先。
 */
export const composeTaskTitle = (taskCode?: string | null, title?: string | null): string => {
    if (title) {
        return title;
    }
    return taskCode ? String(t(`workbench.tasks.${taskCode}.name`, { defaultValue: taskCode })) : '';
};

/** 任务副标题，口径同 [composeTaskTitle]：人工指派用上游描述，模板任务按 code 出 i18n */
export const composeTaskSubtitle = (taskCode?: string | null, detail?: string | null): string => {
    if (detail) {
        return detail;
    }
    return taskCode ? String(t(`workbench.tasks.${taskCode}.subtitle`, { defaultValue: '' })) : '';
};

/** 星期文案，取 i18n 的 workbench.weekdays 数组 */
const weekdayLabel = (parsed: dayjs.Dayjs): string => {
    const weekdays = t('workbench.weekdays', { returnObjects: true }) as unknown as string[];
    return Array.isArray(weekdays) ? weekdays[parsed.day()] : '';
};

/**
 * 派发时间文案，如「周五派发」。
 *
 * timeZone 取 hubDailyReview 下发的片区时区（上游权威源）：派发面板与页头的复盘日同属
 * 片区口径，按浏览器时区渲染会让跨时区的片区经理看到与复盘日差一天的星期。
 * 缺时区返回空串，由调用方省略该段，口径同 [formatLocalTime]。
 */
export const composeAssignedText = (assignedAt?: string | null, timeZone?: string | null): string => {
    if (!assignedAt || !timeZone) {
        return '';
    }
    const parsed = dayjs(assignedAt).tz(timeZone);
    return parsed.isValid()
        ? String(t('hubReview.assigned.assignedOn', { weekday: weekdayLabel(parsed) }))
        : '';
};

/** 截止文案，如「今日 08:00 截止」。时区口径同 [composeAssignedText] */
export const composeDueText = (dueAt?: string | null, timeZone?: string | null): string => {
    if (!dueAt || !timeZone) {
        return '';
    }
    const parsed = dayjs(dueAt).tz(timeZone);
    if (!parsed.isValid()) {
        return '';
    }
    const time = parsed.format('HH:mm');
    // 「今日 / 明日」也按片区时区判定，否则会出现「今日 08:00 截止」而实际是片区的明天
    const dayOffset = parsed.startOf('day').diff(dayjs().tz(timeZone).startOf('day'), 'day');
    if (dayOffset === 0) {
        return String(t('hubReview.assigned.dueToday', { time }));
    }
    if (dayOffset === 1) {
        return String(t('hubReview.assigned.dueTomorrow', { time }));
    }
    return String(t('hubReview.assigned.dueOnWeekday', { weekday: weekdayLabel(parsed), time }));
};

/**
 * 时间戳按仓本地时区格式化为 HH:mm。
 *
 * 缺时区一律返回空串，由调用方降级为 `—`：仓管看到的是仓本地时刻，
 * 回落浏览器时区会把「时间错了」伪装成「时间对了」，比不显示危害更大。
 */
export const formatLocalTime = (isoTime: string, timeZone?: string | null): string => {
    if (!timeZone) {
        return '';
    }
    const parsed = dayjs(isoTime).tz(timeZone);
    return parsed.isValid() ? parsed.format('HH:mm') : '';
};

/** 同 [formatLocalTime]，跨日场景补出日期 */
export const formatLocalDateTime = (isoTime: string, timeZone?: string | null): string => {
    if (!timeZone) {
        return '';
    }
    const parsed = dayjs(isoTime).tz(timeZone);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '';
};

/**
 * 把截止时刻渲染成带「今日 / 明日 / 周X」限定的文案。
 *
 * 只出 `HH:mm` 会让跨日截止被读成同一天 —— 夜班 SOP 截止到次日 04:00 时，
 * 「04:00 前」看起来比同日的「06:00 前」更早，横幅取最晚截止也就成了「取错了」。
 * 日界按仓时区判定，口径同 [formatLocalTime]；缺时区返回空串由调用方降级。
 */
const composeDayScopedTime = (
    endAt: string,
    timeZone: string | null | undefined,
    keys: { today: string; tomorrow: string; weekday: string }
): string => {
    if (!timeZone) {
        return '';
    }
    const parsed = dayjs(endAt).tz(timeZone);
    if (!parsed.isValid()) {
        return '';
    }
    const time = parsed.format('HH:mm');
    const dayOffset = parsed.startOf('day').diff(dayjs().tz(timeZone).startOf('day'), 'day');
    if (dayOffset === 0) {
        return String(t(keys.today, { time }));
    }
    if (dayOffset === 1) {
        return String(t(keys.tomorrow, { time }));
    }
    // 含已逾期的昨日及更早，一律出星期，不再造「昨日 / N 天前」第三套口径
    return String(t(keys.weekday, { weekday: weekdayLabel(parsed), time }));
};

/**
 * 任务数量的计量单位，按上游 `SopSubType` 逐个约定。
 *
 * 未收录的 taskCode（含人工派发的 `GENERAL`）出纯数字：不编造单位，也不因为不认识就把数量丢掉。
 * 司机出仓审核（`SOP_DRIVER_DEPART_AUDIT`）复核的是「路线件数」这类混合口径，产品定为纯数字。
 */
const UNIT_KEY_BY_TASK_CODE: Record<string, string> = {
    SOP_INBOUND_BAG_SCAN: 'workbench.taskText.unitBag',
    SOP_RETURN_INBOUND_SORT: 'workbench.taskText.unitPcs',
    SOP_EXCEPTION_SUSPECTED_LOST: 'workbench.taskText.unitPcs',
    SOP_EXCEPTION_NO_UPDATE: 'workbench.taskText.unitPcs',
    SOP_RETURN_PUTAWAY: 'workbench.taskText.unitPcs',
    SOP_OFFSHELF_HANDLE: 'workbench.taskText.unitPcs',
    SOP_CS_TICKET: 'workbench.taskText.unitTicket',
};

/** 「做完即可」的任务，上游的数量字段对仓管没有意义，一律不展示 */
const TASK_CODES_WITHOUT_COUNT = new Set([
    'SOP_LINEHAUL_CHECKIN',
    'SOP_RETURN_LOADOUT',
    'SOP_SITE_CLEARANCE',
]);

const unitTextOf = (taskCode?: string | null): string => {
    const key = taskCode ? UNIT_KEY_BY_TASK_CODE[taskCode] : undefined;
    return key ? String(t(key)) : '';
};

const hasCount = (taskCode?: string | null, progressTotal?: number | null): boolean =>
    progressTotal !== null
    && progressTotal !== undefined
    && !(!!taskCode && TASK_CODES_WITHOUT_COUNT.has(taskCode));

/** 总量文案，如「共 142 件」；该任务不计数时返回空串，由调用方决定是否显示占位符 */
export const composeVolumeText = (
    progressTotal?: number | null,
    taskCode?: string | null
): string => {
    if (!hasCount(taskCode, progressTotal)) {
        return '';
    }
    const unit = unitTextOf(taskCode);
    return unit
        ? String(t('workbench.taskText.volumeTotal', { count: progressTotal, unit }))
        : String(t('workbench.taskText.volumeTotalPlain', { count: progressTotal }));
};

/** 数量列文案，如「62/142 件」；该任务不计数时返回空串 */
export const composeCountText = (
    progressCurrent?: number | null,
    progressTotal?: number | null,
    taskCode?: string | null
): string => {
    if (!hasCount(taskCode, progressTotal)) {
        return '';
    }
    const unit = unitTextOf(taskCode);
    const done = progressCurrent ?? 0;
    return unit
        ? String(t('workbench.taskText.countRatio', { done, total: progressTotal, unit }))
        : String(t('workbench.taskText.countRatioPlain', { done, total: progressTotal }));
};

/**
 * 时间窗文案，如「今日 10:30 前」。
 *
 * 无截止时刻表示该任务无时限，按「尽快」处理 —— 原先上游用字面量 `'ASAP'` 表达，
 * 现由本函数统一出 i18n 文案，调用方不再比对字符串。
 */
export const composeWindowText = (windowEndAt?: string | null, timeZone?: string | null): string => {
    if (!windowEndAt) {
        return String(t('workbench.asap'));
    }
    const text = composeDayScopedTime(windowEndAt, timeZone, {
        today: 'workbench.taskText.windowToday',
        tomorrow: 'workbench.taskText.windowTomorrow',
        weekday: 'workbench.taskText.windowOnWeekday',
    });
    // 有截止时刻但算不出仓本地时间时出 —，不能退回「尽快」：那是把「不知道」说成「无时限」
    return text || '—';
};

/**
 * 剩余时长文案。超过一小时的一律折成「剩 23h 1min」——
 * 「剩 1381 分」要仓管自己心算才知道是「今天来得及」还是「马上要爆」。
 */
export const composeRemainingText = (minutes: number): string => {
    if (minutes < 60) {
        return String(t('workbench.minutesLeft', { count: minutes }));
    }
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0
        ? String(t('workbench.hoursLeft', { hours }))
        : String(t('workbench.hoursMinutesLeft', { hours, minutes: rest }));
};

/** 该任务是否无时限（原 `windowText === 'ASAP'` 的判定，用于标红紧急） */
export const isAsapTask = (windowEndAt?: string | null): boolean => !windowEndAt;

/** 截止提示，如「明日 04:00 截止」；无截止时刻返回空串 */
export const composeDeadlineLabel = (windowEndAt?: string | null, timeZone?: string | null): string => {
    if (!windowEndAt) {
        return '';
    }
    return composeDayScopedTime(windowEndAt, timeZone, {
        today: 'workbench.taskText.deadlineToday',
        tomorrow: 'workbench.taskText.deadlineTomorrow',
        weekday: 'workbench.taskText.deadlineOnWeekday',
    });
};

/**
 * 已完成文案，如「已完成 62 件」。
 *
 * 总量缺失或为 0 时不出文案：没有分母时「已完成 62」无法让仓管判断还剩多少，
 * 不如不显示，由进度条本身缺省表达。
 */
export const composeProgressText = (
    progressCurrent?: number | null,
    progressTotal?: number | null,
    taskCode?: string | null
): string => {
    if (!hasCount(taskCode, progressTotal) || (progressTotal ?? 0) <= 0) {
        return '';
    }
    const unit = unitTextOf(taskCode);
    const done = progressCurrent ?? 0;
    return unit
        ? String(t('workbench.taskText.doneWithUnit', { done, unit }))
        : String(t('workbench.taskText.donePlain', { done }));
};
